/**
 * Lets a routed page replace the top-bar breadcrumb trail.
 *
 * The default trail is derived from the URL, which is fine for flat routes but useless for
 * /app/templates/:templateId — that renders "Templates › 8f3a1b2c-…", a raw GUID with no hint of
 * where the template actually lives. A page that knows better (it has the folder chain loaded)
 * publishes the real trail here and AppLayout renders it instead.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

export interface Crumb {
  label: string
  /** Omit to render as plain text — used for the current item, the trail's leaf. */
  to?: string
}

interface BreadcrumbValue {
  trail: Crumb[] | null
  setTrail: (crumbs: Crumb[] | null) => void
}

const BreadcrumbContext = createContext<BreadcrumbValue | null>(null)

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [trail, setTrail] = useState<Crumb[] | null>(null)
  const value = useMemo(() => ({ trail, setTrail }), [trail])
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
}

/** Read the published trail. Null means "fall back to the route-derived one". */
export function useBreadcrumb(): BreadcrumbValue {
  const ctx = useContext(BreadcrumbContext)
  if (!ctx) throw new Error('useBreadcrumb must be used within BreadcrumbProvider')
  return ctx
}

/**
 * Publishes a trail for as long as the calling component is mounted, clearing it on unmount so the
 * next page starts from the route-derived default.
 *
 * Pass null while the data is still loading — that keeps the route-derived trail visible rather
 * than flashing an empty bar.
 */
export function usePublishBreadcrumb(crumbs: Crumb[] | null): void {
  const { setTrail } = useBreadcrumb()

  // `crumbs` is a fresh array on every render, so depend on its content, not its identity.
  const key = crumbs === null ? null : JSON.stringify(crumbs)

  useEffect(() => {
    setTrail(key === null ? null : (JSON.parse(key) as Crumb[]))
    return () => setTrail(null)
  }, [key, setTrail])
}
